import { useGetContentItemAncestorsQuery } from '@api/api';
import { ContentItemRequest, EpiPage } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { BlockContainer, LoadingError, LoadingSkeleton } from '@components';
import { useInternalRedirect } from '@hooks';
import { useAppSelector } from '@hooks/redux/store.hooks';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { Box, Link as MdLink, styled } from '@mui/material';
import {
  getCurrentPageContentType,
  getCurrentPageDisplayName,
  getCurrentPageId,
  getCurrentPageName,
  getItemERPDescription,
  getSpecialPage,
} from '@store/app/app.slice';
import { t } from '@translations';
import {
  ContentTypes,
  getAncestorsWithoutRootPage,
  isCatalogContentType,
  isCategoryContentType,
} from '@utils/content.utils';
import { getInternalPath } from '@utils/links.utils';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

const StyledBreadCrumbs = styled(Box)`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  min-height: 24px;
  padding-top: 30px;
  padding-bottom: 24px;
`;
const StyledDelimiter = styled(ArrowForward)`
  color: #9ca2a5;
  width: 16px;
  height: 16px;
`;

const StyledLink = styled(MdLink)`
  color: #b4b4b4;
  cursor: pointer;
  text-decoration: none;
`;

const StyledCurrentItem = styled('span')`
  font-weight: bold;
`;

const BreadCrumbsItem = styled(Box)`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const visibleCatalogContentTypes = [
  ContentTypes.Node,
  ContentTypes.Category,
  ContentTypes.Product,
  ContentTypes.Variation,
];

export const BreadCrumbsBlock = function () {
  const location = useLocation();
  const currentPageGuid = useAppSelector(getCurrentPageId);
  const currentPageName = useAppSelector(getCurrentPageName);
  const currentPageDisplayName = useAppSelector(getCurrentPageDisplayName);
  const currentPageContentType = useAppSelector(getCurrentPageContentType);
  const currentPageIsCatalogContent = useMemo(
    () => isCatalogContentType(currentPageContentType),
    [currentPageContentType]
  );
  const currentPageIsCategoryContent = useMemo(
    () => isCategoryContentType(currentPageContentType),
    [currentPageContentType]
  );

  const itemERPDescription = useAppSelector(getItemERPDescription);
  const homePagePageUrl = useAppSelector(getSpecialPage('homePage'));
  const homePagePageGuid = useAppSelector(getSpecialPage('homePageGuid'));
  const homePagePageName = useAppSelector(getSpecialPage('homePageName'));

  const isVariationContentType = currentPageContentType?.includes(ContentTypes.Variation);
  const isProductContentType = currentPageContentType?.includes(ContentTypes.Product);

  const homePage: EpiPage = {
    name: homePagePageName,
    url: homePagePageUrl,
    contentType: [ContentTypes.HomePage],
    contentLink: {
      guidValue: homePagePageGuid,
    },
  };

  const request: ContentItemRequest = { guid: currentPageGuid };
  const response = useGetContentItemAncestorsQuery(request, {
    skip: !currentPageGuid,
  });
  const { isError, isLoading, data } = response;

  const isHomePage = homePagePageGuid === currentPageGuid;
  const allBreadCrumbs = getAncestorsWithoutRootPage(data ?? [], isHomePage ? undefined : homePage);

  const contentLinks = currentPageIsCatalogContent
    ? allBreadCrumbs.filter(
        (x) => x.contentType?.some((y) => visibleCatalogContentTypes.includes(y)) ?? false
      )
    : allBreadCrumbs;

  const viewModel = {
    currentPageName: currentPageDisplayName ?? currentPageName,
    links: contentLinks.map(({ contentLink, url, name, displayName }) => ({
      guidValue: contentLink?.guidValue,
      url,
      name: displayName ? displayName : name,
    })),
  };

  const navigate = useInternalRedirect();
  const handleClick = (url: any) => () => {
    if (url) {
      const path = getInternalPath(url) ?? '/';
      navigate(path);
    }
  };

  const checkRenderBreadcrumb = (guidValue?: string) => {
    if (guidValue === homePagePageGuid) return false;
    return true;
  };

  const HomeBreadcrumb = (
    <BreadCrumbsItem component='span'>
      <StyledLink onClick={handleClick('/')}>{t(TextCodes.Base.Home)}</StyledLink>
      <StyledDelimiter />
    </BreadCrumbsItem>
  );

  if (location.pathname !== '/') {
    return (
      <BlockContainer noAddedPadding>
        <StyledBreadCrumbs className='breadcrumb-wrapper'>
          {isLoading && <LoadingSkeleton />}
          {!isError && currentPageIsCategoryContent && (
            <>
              {HomeBreadcrumb}
              <CategoryBreadcrumbs />
            </>
          )}
          {!currentPageIsCategoryContent && !isError && viewModel.links.length > 0 && (
            <>
              {HomeBreadcrumb}
              {viewModel.links.map(({ guidValue, url, name }) => {
                return (
                  checkRenderBreadcrumb(guidValue) && (
                    <BreadCrumbsItem component='span' key={guidValue}>
                      <StyledLink onClick={handleClick(url)}>{name}</StyledLink>
                      <StyledDelimiter />
                    </BreadCrumbsItem>
                  )
                );
              })}
              {isVariationContentType || isProductContentType ? (
                <StyledCurrentItem>{itemERPDescription}</StyledCurrentItem>
              ) : (
                <StyledCurrentItem>{viewModel.currentPageName}</StyledCurrentItem>
              )}
            </>
          )}
          {!currentPageIsCategoryContent && !isError && viewModel.links.length === 0 && (
            <>
              {HomeBreadcrumb}
              <StyledCurrentItem>{viewModel.currentPageName}</StyledCurrentItem>
            </>
          )}
          {isError && currentPageGuid && <LoadingError />}
        </StyledBreadCrumbs>
      </BlockContainer>
    );
  }
  return null;
};

const CategoryBreadcrumbs = () => {
  const navigate = useInternalRedirect();
  const breadcrumbs = useAppSelector((state) => state.catalog.breadcrumbs);

  const handleClick = (url: any) => () => {
    if (url) {
      const path = getInternalPath(url) ?? '/';
      navigate(path);
    }
  };

  if (!breadcrumbs || breadcrumbs.length === 0) {
    return null;
  }

  return (
    <>
      {breadcrumbs?.map(({ displayName, url }, index) => {
        const isLast = index === breadcrumbs.length - 1;
        return (
          <BreadCrumbsItem component='span' key={index}>
            {!isLast ? (
              <>
                <StyledLink onClick={handleClick(url)}>{displayName}</StyledLink>
                <StyledDelimiter />
              </>
            ) : (
              <StyledCurrentItem>{displayName}</StyledCurrentItem>
            )}
          </BreadCrumbsItem>
        );
      })}
    </>
  );
};
